import { ReactElement, ReactNode } from 'react';
import { SettingsValueProps } from '../components/settings/types';

declare global {
  interface Window {
    BRAND_TITLE: string;
    locale: string;
  }
}

export const domainBVR = 'guest.beachviewrentals.com';
export const domainMRR = 'guest.mauiresortrentals.com';

export enum BrandCode {
  BVR = 'BVR',
  DRR = 'DRR',
  MRR = 'MRR',
}

export interface PromoCode {
  id: string;
  link: string;
  sortDescription: string;
  description: string;
  label: string;
  background?: string;
  inverted?: boolean;
  character?: string;
}

export interface BrandConfig {
  name: string;
  greeting: string;
  thankyou: string;
  brandId: string;
  brandCode: BrandCode;
  backgroundUrl?: string;
  logoUrl: string;
  iconUrl: string;
  logoUrlDark: string;
  iconUrlDark: string;
  themeSettings: SettingsValueProps;
  customCssUrl?: string; // Optional, for custom styles if needed
  address: {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
  };
  website: string;
  supportEmail: string;
  supportPhone: string;
  locale: string;
  timezone: string;
  timezoneCode: string;
  stripePublishableKey: string;
  travelInsuranceEnabled: boolean;
  allowCheckInRequests: boolean;
  helpLinks: Record<string, string>; // Links for guest registration and ID verification
  promoCodes?: PromoCode[];
}

// Define your branding configurations for different domains here
const domainBrand: Record<string, BrandConfig> = {
  [domainBVR]: {
    name: 'Beachview Rentals',
    greeting: 'Welcome',
    thankyou: 'Thank you',
    brandId: 'beach_view_rentals',
    brandCode: BrandCode.BVR,
    backgroundUrl: '/assets/background/bg_img3.webp',
    logoUrl: '/logo/bvr_logo_full.svg',
    iconUrl: '/logo/bvr_logo_small.svg',
    logoUrlDark: '/logo/bvr_logo_full_dark.svg',
    iconUrlDark: '/logo/bvr_logo_small_dark.svg',
    website: 'https://www.beachviewrentals.com',
    supportEmail: 'experience@beachviewrentals.com',
    supportPhone: '(949) 673-2627',
    locale: 'Newport Beach',
    timezone: 'America/Los_Angeles',
    timezoneCode: 'PT',
    address: {
      streetAddress: '2210 Newport Blvd. Suite 200',
      city: 'Newport Beach',
      state: 'CA',
      postalCode: '92663',
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeContrast: 'default',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false,
    },
    stripePublishableKey: process.env.REACT_APP_BVR_STRIPE_PUBLISHABLE_KEY as string,
    travelInsuranceEnabled: true,
    allowCheckInRequests: false,
    helpLinks: {
      guest_registration:
        'https://drive.google.com/file/d/1bvtU9tamfyFMObstPgYkKo09UI7rJIU-/preview',
      guest_id_verification:
        'https://drive.google.com/file/d/1CIstEOxZCv4AifL5ZNy16qhRqLWgJbTB/preview',
    },
  },
  [domainMRR]: {
    name: 'Maui Resort Rentals',
    greeting: 'Aloha',
    thankyou: 'Mahalo',
    brandId: 'maui_resort_rentals',
    brandCode: BrandCode.MRR,
    backgroundUrl: '/assets/background/bg_img1_33pct.jpg',
    logoUrl: '/logo/mrr_logo_full.svg',
    iconUrl: '/logo/mrr_logo_small.svg',
    logoUrlDark: '/logo/mrr_logo_full_dark.svg',
    iconUrlDark: '/logo/mrr_logo_small_dark.svg',
    website: 'https://www.mauiresortrentals.com',
    supportEmail: 'experience@mauiresortrentals.com',
    supportPhone: '(888) 468-0578',
    locale: 'Maui',
    timezone: 'Pacific/Honolulu',
    timezoneCode: 'HT',
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761',
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeContrast: 'default',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false,
    },
    stripePublishableKey: process.env.REACT_APP_MRR_STRIPE_PUBLISHABLE_KEY as string,
    travelInsuranceEnabled: true,
    allowCheckInRequests: true,
    helpLinks: {
      guest_registration:
        'https://drive.google.com/file/d/1momSK4VJVaerLGdHIMz3tB_dQS5dVaZS/preview',
      guest_id_verification:
        'https://drive.google.com/file/d/1P8Tmu-jpfX1S2dyNHqJ380SNhFmLB1fZ/preview',
    },
    promoCodes:[{
    label:'Have you booked your flight yet?',
    sortDescription: "Book Hawaiian Airlines Promo",
    description: "Unlock a special discount on your Hawaiian Airlines booking with this exclusive link. It’s our way of saying mahalo for flying with us. But don’t delay—this limited-time offer won’t be around for long!",
    link:'https://www.hawaiianairlines.com/affiliate?affcode=MRREALTY',
    // inverted: true,
    background: '/assets/background/overlay_3.jpg',
    character: '/assets/illustrations/characters/character_1.png',
    id: 'hawaiian_airlines_promo'
    }]
  },
  default: {
    name: 'Dream Resorts',
    greeting: 'Aloha',
    thankyou: 'Mahalo',
    brandId: 'maui_resort_rentals',
    brandCode: BrandCode.DRR,
    backgroundUrl: '/assets/background/bg_img2.webp',
    logoUrl: '/logo/mrr_logo_full.svg',
    iconUrl: '/logo/mrr_logo_small.svg',
    logoUrlDark: '/logo/mrr_logo_full_dark.svg',
    iconUrlDark: '/logo/mrr_logo_small_dark.svg',
    website: 'https://www.mauiresortrentals.com',
    supportEmail: 'experience@mauiresortrentals.com',
    supportPhone: '(888) 468-0578',
    locale: 'Maui',
    timezone: 'Pacific/Honolulu',
    timezoneCode: 'HT',
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761',
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeContrast: 'default',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false,
    },
    stripePublishableKey: process.env.REACT_APP_DRR_STRIPE_PUBLISHABLE_KEY as string,
    travelInsuranceEnabled: true,
    allowCheckInRequests: false,
    helpLinks: {},
  },
};

// Determine the current hostname, environment variable will override
const currentHostname = process.env.REACT_APP_HOSTNAME || window.location.hostname;

// Select the brand configuration based on the current hostname
const brandConfig = domainBrand[currentHostname] || domainBrand.default; // Fallback to 'default' if no match

export default brandConfig;
